import React, { useMemo } from "react"

import {
    COMMON_ONE_VERIFY_POLICIES_CONFIG,
    PoliciesListType,
    PolicyAssignmentPriority,
    PolicyAssignmentStatus,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { ButtonElement } from "@front-shared/elements/button.element"
import { NirvanaBreathingLoaderElement } from "@front-shared/elements/nirvanaBreathingLoader.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { SelectElement } from "@front-shared/elements/select.element"
import { ConfirmationHelper } from "@front-shared/helpers/confirmation.helper"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper"

import { GENERAL_CONFIG } from "../../config/general.config"
import { POLICIES_CONFIG } from "../../config/policies.config"
import { PoliciesHelper } from "../../helpers/policies.helper"
import { RuntimeHelper } from "../../helpers/runtime.helper"
import { usePoliciesList } from "../../hooks/policiesList.hook"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { policiesSelector } from "../../store/selectors/policies.selector"
import { profileSelector } from "../../store/selectors/profile.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import {
    taskAssignmentsBulkUpdatePriority,
    taskAssignmentsBulkUpdateStatus,
} from "../../store/thunks/taskAssignments.thunks"
import { IAssignmentIdentification } from "../../types/objects.types"

import closeIcon from "@front-shared/assets/images/icons/close-dark.svg"

export const BulkTaskAssignmentControlComponent: React.FunctionComponent<
    IBulkTaskAssignmentControlComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const runtimeState = useAppSelector(runtimeSelector)

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const policiesState = useAppSelector(policiesSelector)

    const profile = useAppSelector(profileSelector)

    const { onSuccessReloadList } = usePoliciesList()

    const isLoading = useMemo<boolean>(() => {
        return RuntimeHelper.isGettingFullPoliciesListLoading()
    }, [runtimeState.isLoading])

    const isAssigningTasks = useMemo<boolean>(() => {
        return RuntimeHelper.isTaskAssignmentsBulkAssignLoading()
    }, [runtimeState.isLoading])

    const isUpdatingStatus = useMemo<boolean>(() => {
        return (
            RuntimeHelper.isTaskAssignmentsBulkUpdateStatusLoading() ||
            RuntimeHelper.isTaskAssignmentsBulkUpdatePriorityLoading() ||
            RuntimeHelper.isTaskAssignmentsGetListLoading()
        )
    }, [runtimeState.isLoading])

    const renderedStatusOptions = useMemo(() => {
        const mapping = POLICIES_CONFIG.policyAssignmentStatusMapping(
            props.listType,
        )

        // Map each status to a dropdown option, returning null for statuses not in the mapping
        // Then filter out null values to only show statuses available for this listType
        return POLICIES_CONFIG.uiAllowedAssignmentStatuses
            .map(status => {
                const label = mapping[status]

                return label
                    ? {
                          label,
                          value: status,
                          displayValue: label,
                      }
                    : null
            })
            .filter(
                (option): option is NonNullable<typeof option> =>
                    option !== null,
            )
    }, [props.listType])

    const onStatusSelected = (status?: PolicyAssignmentStatus) => {
        if (!status) {
            return
        }

        const mappedStatus =
            POLICIES_CONFIG.policyAssignmentStatusMapping(props.listType)[
                status
            ] ?? status

        // Build array of assignments that need status changes
        // Business rule: Use config-driven forbidden transitions
        const assignmentToChangeStatus = props.coverageChecks.reduce<
            IAssignmentIdentification[]
        >((acc, item) => {
            if (
                !item.policy?.latestAssignment?.id ||
                item.policy.latestAssignment.status === status ||
                !PoliciesHelper.isStatusTransitionAllowed(
                    item.policy.latestAssignment.status,
                    status,
                )
            ) {
                // Skip if no assignment, already has the status, or transition not allowed
                return acc
            }

            acc.push({
                id: item.policy.latestAssignment.id,
                nirvanaRequestId: item.coverageResult.resultId,
            })

            return acc
        }, [])

        // Count assignments that were skipped due to rules above
        const skippedAssignmentsCount = props.coverageChecks.reduce<number>(
            (count, item) => {
                if (
                    !item.policy?.latestAssignment?.id ||
                    item.policy.latestAssignment.status === status ||
                    !PoliciesHelper.isStatusTransitionAllowed(
                        item.policy.latestAssignment.status,
                        status,
                    )
                ) {
                    return count + 1
                }

                return count
            },
            0,
        )

        const toChangeCount = assignmentToChangeStatus.length

        // If there are no assignments to change status, show a warning and do not show the confirmation popup
        if (!toChangeCount) {
            // Show a warning
            ToastrHelper.warning(
                "All selected policies already have this status. No updates are needed.",
                GENERAL_CONFIG.defaultToastrTimeout,
                undefined,
            )

            props.onCancel()

            return
        }

        // Confirmation message
        let message = `You will change ${toChangeCount} policy ${toChangeCount > 1 ? "statuses" : "status"} to "${mappedStatus}". `

        if (skippedAssignmentsCount > 0) {
            message += `${skippedAssignmentsCount} policies will be skipped due to invalid status transitions.`
        } else if (props.coverageChecks.length !== toChangeCount) {
            message +=
                "Not all selected policies will update. Updates apply only if a policy has an Assignee, the new Status differs from the current one, and the current Status is not done."
        }

        message += " Confirm to apply this change."

        ConfirmationHelper.enableConfirmation(
            async () => {
                if (!selectedPracticeRole || !toChangeCount) {
                    return
                }

                try {
                    dispatch(
                        taskAssignmentsBulkUpdateStatus({
                            practice: selectedPracticeRole.practice,
                            payload: {
                                practice: selectedPracticeRole.practice,
                                assignments: assignmentToChangeStatus,
                                status,
                            },
                            onSuccess: (
                                results: (
                                    | IOneVerifyCoverageCheckHistory
                                    | undefined
                                )[],
                            ) => {
                                onSuccessReloadList(props.listType, results)

                                props.onCancel()
                            },
                        }),
                    )
                } catch (error) {}
            },
            "Confirm Status Change",
            message,
            props.onCancel,
        )
    }

    const onPrioritySelected = (priority?: PolicyAssignmentPriority) => {
        if (!priority) {
            return
        }

        const mappedPriority =
            POLICIES_CONFIG.policyAssignmentPriorityMapping(props.listType)[
                priority
            ] ?? priority

        // Build array of assignments that need priority changes
        // Business rule: Use config-driven forbidden transitions
        const assignmentToChangePriority = props.coverageChecks.reduce<
            IAssignmentIdentification[]
        >((acc, item) => {
            if (
                !item.policy?.latestAssignment?.id ||
                item.policy.latestAssignment.priority === priority
            ) {
                // Skip if no assignment, already has the priority, or transition not allowed
                return acc
            }

            acc.push({
                id: item.policy.latestAssignment.id,
                nirvanaRequestId: item.coverageResult.resultId,
            })

            return acc
        }, [])

        const toChangeCount = assignmentToChangePriority.length

        // If there are no assignments to change priority, show a warning and do not show the confirmation popup
        if (!toChangeCount) {
            // Show a warning
            ToastrHelper.warning(
                "All selected policies already have this priority. No updates are needed.",
                GENERAL_CONFIG.defaultToastrTimeout,
                undefined,
            )

            props.onCancel()

            return
        }

        // Confirmation message
        let message = `You will change ${toChangeCount} policy ${toChangeCount > 1 ? "priorities" : "priority"} to "${mappedPriority}". `

        if (props.coverageChecks.length !== toChangeCount) {
            message +=
                "Not all selected policies will update. Updates apply only if a policy has an Assignee and the new Priority differs from the current one."
        }

        message += " Confirm to apply this change."

        ConfirmationHelper.enableConfirmation(
            async () => {
                if (!selectedPracticeRole || !toChangeCount) {
                    return
                }

                try {
                    dispatch(
                        taskAssignmentsBulkUpdatePriority({
                            practice: selectedPracticeRole.practice,
                            payload: {
                                practice: selectedPracticeRole.practice,
                                assignments: assignmentToChangePriority,
                                priority,
                            },
                            onSuccess: (
                                results: (
                                    | IOneVerifyCoverageCheckHistory
                                    | undefined
                                )[],
                            ) => {
                                onSuccessReloadList(props.listType, results)

                                props.onCancel()
                            },
                        }),
                    )
                } catch (error) {}
            },
            "Confirm Priority Change",
            message,
            props.onCancel,
        )
    }

    const isSelectedActiveAssignmentStatus = () => {
        return props.coverageChecks.some(coverageCheck => {
            return (
                coverageCheck?.policy?.latestAssignment?.status &&
                POLICIES_CONFIG.activeAssignmentStatuses.includes(
                    coverageCheck.policy.latestAssignment.status,
                )
            )
        })
    }

    // Check if any selected policies have done-status assignments assigned to current user
    const hasDoneStatusSelfAssigned = useMemo(() => {
        if (!profile?.id) {
            return false
        }

        return props.coverageChecks.some(coverageCheck => {
            const assignment = coverageCheck?.policy?.latestAssignment

            if (!assignment?.status || !assignment.assignee?.id) {
                return false
            }

            const isDoneStatus =
                COMMON_ONE_VERIFY_POLICIES_CONFIG.doneAssignmentStatuses.includes(
                    assignment.status,
                )

            return isDoneStatus && assignment.assignee.id === profile.id
        })
    }, [props.coverageChecks, profile?.id])

    // Check if any selected policies have no assignment
    const hasNoAssignment = useMemo(() => {
        return props.coverageChecks.some(
            coverageCheck => !coverageCheck?.policy?.latestAssignment,
        )
    }, [props.coverageChecks])

    // Only show "Assign Policies" button if withBulkAssign prop is true
    // In biller-only mode: billers can assign themselves to done/unassigned, but cannot reassign/unassign
    // Managers: can assign, reassign, and unassign all policies
    //
    // Notes from product:
    // Managers can also unassign on both pages. Managers can also change status on both pages.
    // For biller, they can only change status in Assigned Tasks page.
    // For billers, the can only assign themselves to unassigned or done tasks that were most recently done by them.
    // For billers, they should only be able to see assigned tasks to them and unassigned in Task Assignment page; and only assigned to them in Assigned Tasks
    const canAssignReassign = useMemo<boolean>(() => {
        switch (props.isBillerOnlyMode) {
            case true:
                // for billers: when parent allows bulk assignments + (hasDoneStatusSelfAssigned || hasNoAssignment)
                return (
                    !!props.withBulkAssign &&
                    (hasDoneStatusSelfAssigned || hasNoAssignment)
                )

            default:
                // for managers: just when parent allows bulk assignments
                return !!props.withBulkAssign
        }
    }, [
        props.withBulkAssign,
        props.isBillerOnlyMode,
        hasDoneStatusSelfAssigned,
        hasNoAssignment,
    ])

    // Only allow status change if there are active assignments (not done-status)
    const canChangeStatus = useMemo(() => {
        return props.withBulkStatus && isSelectedActiveAssignmentStatus()
    }, [props.coverageChecks, props.withBulkStatus])

    // Only allow priority change if there are active assignments (not done-status)
    // Priority changes require manageTaskAssignments permission (managers only)
    const canChangePriority = useMemo(() => {
        return props.withBulkPriority && isSelectedActiveAssignmentStatus()
    }, [props.coverageChecks, props.withBulkPriority])

    // Billers cannot unassign, only managers can
    // Only allow unassign if there are active assignments (not done-status)
    const canUnassign = useMemo(() => {
        return (
            !props.isBillerOnlyMode &&
            canAssignReassign &&
            isSelectedActiveAssignmentStatus()
        )
    }, [props.coverageChecks, canAssignReassign, props.isBillerOnlyMode])

    const sharedStatusDisplay = useMemo(() => {
        if (props.coverageChecks.length === 0) {
            return undefined
        }

        // Get all unique statuses from selected policies
        const statuses = props.coverageChecks
            .map(
                coverageCheck =>
                    coverageCheck?.policy?.latestAssignment?.status,
            )
            .filter(status => !!status)

        // If no statuses found or not all same, return undefined
        if (new Set(statuses).size !== 1) {
            return undefined
        }

        const mapping = POLICIES_CONFIG.policyAssignmentStatusMapping(
            props.listType,
        )

        return mapping[statuses[0]] ?? undefined
    }, [props.coverageChecks, props.listType])

    const sharedPriorityDisplay = useMemo(() => {
        if (props.coverageChecks.length === 0) {
            return undefined
        }

        // Get all unique priorities from selected policies
        const priorities = props.coverageChecks
            .map(
                coverageCheck =>
                    coverageCheck?.policy?.latestAssignment?.priority,
            )
            .filter(priority => !!priority)

        // If no statuses found or not all same, return undefined
        if (new Set(priorities).size !== 1) {
            return undefined
        }

        const mapping = POLICIES_CONFIG.policyAssignmentPriorityMapping(
            props.listType,
        )

        return mapping[priorities[0]] ?? undefined
    }, [props.coverageChecks, props.listType])

    return !props.coverageChecks.length ? null : (
        <div className={`relative ${props.className}`}>
            <NirvanaBreathingLoaderElement isActive={isUpdatingStatus} />

            <div
                className="
                    fixed bottom-[16px] horizontal-center flex items-center
                    bg-brand-white rounded-[8px] p-[16px] shadow-7
                    gap-[16px]
                "
            >
                {canAssignReassign && (
                    <ButtonElement
                        label={`Assign Policies ${
                            isLoading
                                ? ` (${policiesState.policiesGettingFullListProgress}%)`
                                : ""
                        }`}
                        onClick={props.onBulkAssign}
                        htmlType="button"
                        type="primary"
                        disabled={isAssigningTasks}
                        isLoading={isLoading}
                    />
                )}

                {canUnassign && (
                    <ButtonElement
                        label="Unassign"
                        onClick={props.onBulkUnassign}
                        htmlType="button"
                        type="default"
                        isDanger
                        disabled={isAssigningTasks}
                        isLoading={isLoading}
                    />
                )}

                {canChangeStatus && (
                    <div>
                        <SelectElement
                            name="bulkStatusSelect"
                            className="min-w-[150px]"
                            externalValue={sharedStatusDisplay}
                            placeholder="Change status"
                            renderedOptions={renderedStatusOptions}
                            onSelected={onStatusSelected}
                            selectClassName="deep-purple-border"
                            size="large"
                            placement="topLeft"
                        />
                    </div>
                )}

                {canChangePriority && (
                    <div>
                        <SelectElement
                            name="bulkPrioritySelect"
                            className="min-w-[150px]"
                            externalValue={sharedPriorityDisplay}
                            placeholder="Change priority"
                            renderedOptions={POLICIES_CONFIG.getRenderedPriorityOptions(
                                props.listType,
                            )}
                            onSelected={onPrioritySelected}
                            selectClassName="deep-purple-border"
                            size="large"
                            placement="topLeft"
                        />
                    </div>
                )}

                {!isLoading && (
                    <>
                        <PrimaryText className="mr-[8px]" typography="text">
                            {props.coverageChecks.length} selected
                        </PrimaryText>

                        <div
                            className="cursor-pointer p-[8px] relative top-[-1px]"
                            onClick={props.onCancel}
                        >
                            <img src={closeIcon} alt="cancel" />
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

interface IBulkTaskAssignmentControlComponentProps {
    className?: string
    coverageChecks: IOneVerifyCoverageCheckHistory[]
    onCancel: () => void
    listType: PoliciesListType
    // status
    withBulkStatus?: boolean
    // priority (managers only)
    withBulkPriority?: boolean
    // assign/reassign/unassign
    withBulkAssign?: boolean
    onBulkAssign?: () => void
    onBulkUnassign?: () => void
    // biller mode
    isBillerOnlyMode?: boolean
}
